import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MessageCircle } from "lucide-react";
import { SERVICES, buildWhatsAppLink, API } from "../../lib/site";

const TIMES = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

const empty = { name: "", phone: "", email: "", service: "Haircuts", date: "", time: "" };

export const BookingDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(empty);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [taken, setTaken] = useState([]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!form.date) { setTaken([]); return; }
    axios.get(`${API}/availability`, { params: { date: form.date } })
      .then((r) => {
        const t = r.data.taken || [];
        setTaken(t);
        setForm((f) => (t.includes(f.time) ? { ...f, time: "" } : f));
      })
      .catch(() => setTaken([]));
  }, [form.date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error("Please fill in your name, phone, date and time.");
      return;
    }
    setLoading(true);
    const payload = { ...form, notes, email: form.email || undefined };
    try {
      await axios.post(`${API}/appointments`, payload);
      toast.success("Booking sent!", {
        description: form.email ? "A confirmation email is on its way. Opening WhatsApp to confirm…" : "Opening WhatsApp to confirm with the studio…",
      });
      window.open(buildWhatsAppLink({ ...form, notes }), "_blank");
      onOpenChange(false);
      setForm(empty);
      setNotes("");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("That time was just taken. Please pick another slot.");
        setTaken((t) => [...new Set([...t, form.time])]);
        setForm((f) => ({ ...f, time: "" }));
      } else {
        toast.error("Something went wrong. Please call us at (514) 292-8780.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border border-[#C9A24B]/40 text-[#FAFAFA] max-w-lg custom-scrollbar max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A24B]">Reserve your chair</p>
          <DialogTitle className="font-serif-display text-3xl font-light tracking-tight">Book an appointment</DialogTitle>
          <DialogDescription className="text-[#8A8A8A] text-sm">Pick your service and time — we'll confirm on WhatsApp.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name">
              <Input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="John Doe" className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
            </Field>
            <Field label="Phone">
              <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="(403) 000-0000" className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
            </Field>
          </div>

          <Field label="Email (for confirmation)">
            <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
          </Field>

          <Field label="Service">
            <Select value={form.service} onValueChange={set("service")}>
              <SelectTrigger data-testid="booking-service" className="bg-[#0A0A0A] border-white/10 focus:ring-[#C9A24B]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-[#FAFAFA]">
                {SERVICES.map((s) => (
                  <SelectItem key={s.id} value={s.name} disabled={s.status !== "available"} data-testid={`booking-service-${s.id}`}>
                    {s.name}{s.status !== "available" ? " — coming soon" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date">
              <Input data-testid="booking-date" type="date" min={today} value={form.date} onChange={set("date")} className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B] [color-scheme:dark]" />
            </Field>
            <Field label={form.date ? "Time" : "Time (pick a date first)"}>
              <Select value={form.time} onValueChange={set("time")} disabled={!form.date}>
                <SelectTrigger data-testid="booking-time" className="bg-[#0A0A0A] border-white/10 focus:ring-[#C9A24B] disabled:opacity-50"><SelectValue placeholder={form.date ? "Select time" : "Select date first"} /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10 text-[#FAFAFA] max-h-52">
                  {TIMES.map((t) => {
                    const isTaken = taken.includes(t);
                    return (
                      <SelectItem key={t} value={t} disabled={isTaken} data-testid={`booking-time-${t}`}>
                        {t}{isTaken ? " — booked" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Notes (optional)">
            <Textarea data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Style preferences, references…" className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B] resize-none" rows={2} />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A24B] text-black text-xs uppercase tracking-[0.2em] font-semibold rounded-sm hover:bg-[#d9b463] transition-colors duration-300 disabled:opacity-60"
          >
            <MessageCircle size={16} />
            {loading ? "Sending…" : "Confirm via WhatsApp"}
          </button>
          <p className="text-[11px] text-center text-[#8A8A8A]">We'll confirm your slot on WhatsApp within business hours.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">{label}</Label>
    {children}
  </div>
);
